import { Clock, RefreshCw, TriangleAlert } from 'lucide-react';
import { useGetMyHistoryQuery, getApiError } from '@/store/api';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EVENT_TONE } from '@safepass/shared';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const stampFmt = new Intl.DateTimeFormat('en-GH', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const LABELS: Record<string, string> = {
  released: 'Released',
  failed_attempt: 'Failed attempt',
  suspicious_flagged: 'Flagged',
  code_revoked: 'Code cancelled',
  delegate_added: 'Delegate added',
  delegate_cancelled: 'Delegate cancelled',
  offline_sync: 'Recorded offline',
};

/*
  Pickup history.

  The tones come from EVENT_TONE in the shared package — the same map the
  dashboard's audit log uses — so a "failed attempt" cannot be amber here and red
  to the administrator looking at the same event.

  Wording is a parent's, not the system's: "Code cancelled", not "code_revoked".
*/
export function ParentHistoryPage() {
  const { data: events = [], isLoading, isError, error, isFetching, refetch } = useGetMyHistoryQuery();

  if (isLoading) return <PageLoader label="Loading history…" />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className={styles.pageTitle}>History</h1>
          <p className={cn(styles.meta, 'mt-0.5')}>Everything that has happened to your children's codes.</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Refresh"
          icon={<RefreshCw size={14} className={isFetching ? 'animate-spin' : undefined} />}
          onClick={() => void refetch()}
        />
      </div>

      <div className={cn(styles.panel, 'overflow-clip')}>
        {isError ? (
          <EmptyState
            icon={<TriangleAlert size={17} />}
            title="Could not load history"
            description={getApiError(error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<Clock size={17} />}
            title="Nothing yet"
            description="Activity appears here after your child's first pickup, or the first time a code is cancelled."
          />
        ) : (
          events.map((ev) => (
            <div
              key={ev.id}
              className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-bold text-foreground">{ev.studentName}</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                  {ev.collectorName
                    ? `Collected by ${ev.collectorName}`
                    : ev.gateOfficerName
                      ? `At the gate · ${ev.gateOfficerName}`
                      : LABELS[ev.eventType] ?? ev.eventType.replace(/_/g, ' ')}
                </p>
                <p className="mt-1 text-[10.5px] tabular-nums text-muted-foreground/80">
                  {stampFmt.format(new Date(ev.createdAt))}
                </p>
              </div>
              <Mark tone={EVENT_TONE[ev.eventType] ?? 'neutral'}>
                {LABELS[ev.eventType] ?? ev.eventType.replace(/_/g, ' ')}
              </Mark>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
